<template>
    <div>
        <q-modal v-model="add_question" :content-css="{minWidth: getMinWidth, maxWidth: getMaxWidth, minHeight: '80vh'}" @show="clearQuestions()" @hide="hideQuestionModal()" no-backdrop-dismiss no-esc-dismiss>
            <q-modal-layout>
                <q-toolbar slot="header">
                    <q-btn flat round dense @click="hideQuestionModal()" icon="fas fa-times" />
                    <q-toolbar-title>
                        {{ $t('question') }}
                    </q-toolbar-title>
                </q-toolbar>

                <div class="layout-padding">
                    <div class="row gutter-sm">
                        <div class="col-xs-12 col-sm-12">
                            <q-input v-model="title" :float-label="$t('title')" :error="$v.title.$error" @input="$v.title.$touch" />
                        </div>
                    </div>

                    <q-table
                        :data="alternatives"
                        :columns="columns"
                        :pagination.sync="server_pagination"
                        @request="request_alternatives"
                        :loading="loading"
                        row-key="id"
                        :no-data-label="$t('no_data_avaliable')"
                        :no-results-label="$t('no_results_found')"
                        :loading-label="$t('loading_data')"
                        :rows-per-page-label="$t('items_per_page')"
                        :rows-per-page-options="[0]"
                        binary-state-sort
                        class="q-mt-lg"
                        >
                        <template slot="top" slot-scope="props">
                            <div class="q-table-title">{{ $t('alternatives') }}</div>
                            <div class="col" />
                            <q-btn color="secondary" icon="fas fa-plus" :label="$t('new')" @click="addAlternative()" :disable="has_results" />
                        </template>

                        <q-tr slot="header" slot-scope="props" :props="props">
                            <q-th v-for="col in props.cols" :key="col.name" :props="props">
                                {{ $t(col.label) }}
                            </q-th>
                        </q-tr>

                        <q-tr slot="body" slot-scope="props" :props="props">
                            <q-td auto-width key="is_correct" :props="props">
                                <q-radio v-model="props.row.is_correct" :val="true" @input="changeCorrect(props.row)" :disable="has_results" />
                            </q-td>

                            <q-td key="title" :props="props">
                                {{ props.row.title }}
                                <q-popup-edit v-model="props.row.title">
                                    <q-input v-model="props.row.title" />
                                </q-popup-edit>
                            </q-td>

                            <q-td auto-width key="actions" :props="props">
                                <q-btn flat round color="negative" icon="far fa-trash-alt" @click="removeAlternative(props.row)" :disable="has_results" />
                            </q-td>
                        </q-tr>
                    </q-table>

                    <div class="row gutter-sm justify-end q-mt-lg">
                        <div class="col-xs-12 col-sm-3" :class="getOrder('cancel')">
                            <q-btn color="tertiary" class="full-width" :label="$t('cancel')" icon="fas fa-ban" @click="hideQuestionModal()" />
                        </div>

                        <div class="col-xs-12 col-sm-3" :class="getOrder('save')">
                            <q-btn color="secondary" class="full-width" :label="$t('save')" icon="far fa-save" @click="saveQuestion()" />
                        </div>
                    </div>
                </div>
            </q-modal-layout>
        </q-modal>

        <q-dialog v-model="add_alternative" @ok="saveAlternative()" @hide="clearAlternative()">
            <span slot="title">{{ $t('add_alternative') }}</span>

            <span slot="message">{{ $t('alternative') }}</span>

            <span slot="body">
                <q-input v-model="new_alternative" @keyup.enter.native="saveAlternative()" />
            </span>

            <template slot="buttons" slot-scope="props">
                <q-btn color="tertiary" :label="$t('cancel')" @click="add_alternative = false" />
                <q-btn color="positive" :label="$t('add_alternative')" @click="saveAlternative()" />
            </template>
        </q-dialog>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { required, requiredIf, maxValue, minValue } from 'vuelidate/lib/validators'

export default {
    props: ['id_test', 'id_question', 'has_results', 'add_question'],

    i18nOptions: { namespaces: [ "custom", "common", "general_tests", "admin"] },

    data() {
        return {
            server_pagination: {
                page: 1,
                rowsNumber: 10,
                rowsPerPage: 0,
                descending: false,
                sortBy: null
            },
            loading: false,
            title: '',
            new_alternative: '',
            add_alternative: false,
            alternatives: [],
            columns: [
                {
                    name: 'is_correct',
                    required: true,
                    label: 'correct',
                    align: 'center',
                    field: 'is_correct',
                    sortable: false,
                },
                {
                    name: 'title',
                    required: true,
                    label: 'alternative',
                    align: 'left',
                    field: 'title',
                    sortable: false,
                },
                {
                    name: 'actions',
                    required: true,
                    label: '',
                    align: 'left',
                    field: 'actions',
                    sortable: false,
                },
            ]
        }
    },

    computed: {
        ...mapGetters({
            access_token: 'common/access_token'
        }),

        getHeader() {
            let headers = {}
            return headers = {
                headers: {
                    'content-type': 'application/json',
                    'authorization': 'Basic ' + btoa(process.env.APP_CLIENT_ID + ':' + process.env.APP_CLIENT_SECRET),
                    'authorization': 'Bearer ' + this.access_token
                }
            }
        },

        getMaxWidth() {
            if (this.$q.platform.is.mobile) return '100vw'
            else return '80vw'
        },

        getMinWidth() {
            if (!this.$q.platform.is.mobile) return '90vw'
        },
    },

    methods: {
        request_alternatives ({ pagination }) {
            this.loading = true
            this.server_pagination = pagination

            let order = pagination.descending ? '-' : ''
            let order_by = pagination.sortBy ? order + pagination.sortBy : ''

            let data = {
                "page_size": this.server_pagination.rowsPerPage,
                "page": this.server_pagination.page,
                "search": this.filter,
                "ordering": order_by
            }

            this.$axios.get(`/admin_general_tests/general_tests/${this.id_test}/general_test_questions/${this.id_question}/?${this.$qs.stringify(data)}`, this.getHeader)
            .then(response => {
                this.server_pagination.rowsNumber = response.data.general_test_question_answers.length
                this.alternatives = []
                this.title = response.data.title

                response.data.general_test_question_answers.forEach((result, index) => {
                    this.alternatives.push({
                        title: result.title,
                        is_correct: result.is_correct,
                        actions: index,
                        id: result.id
                    })
                })

                this.loading = false
            })
            .catch(e => {
                this.loading = false
                console.log(e)
            })
        },

        loadQuestion() {
            this.$q.loading.show()

            this.request_alternatives({ pagination: this.server_pagination })

            this.$axios.get(`/admin_general_tests/general_tests/${this.id_test}/general_test_questions/${this.id_question}/`, this.getHeader)
            .then(response => {
                this.title = response.data.title

                this.$q.loading.hide()
            })
            .catch(e => {
                this.$q.loading.hide()
                console.log(e)
            })
        },

        saveQuestion() {
            this.$v.$touch()

            if (!this.$v.$error && this.alternatives.length > 1) {
                this.$q.loading.show()

                let data = {
                    title: this.title,
                    general_test_question_answers: this.alternatives
                }

                if (this.id_question != 0) {
                    this.$axios.put(`/admin_general_tests/general_tests/${this.id_test}/general_test_questions/${this.id_question}/`, data, this.getHeader)
                    .then(response => {
                        this.$q.loading.hide()
                        this.$v.$reset()

                        this.$q.notify({
                            message: this.$t('question_edit_success'),
                            type: 'positive',
                            icon: 'far fa-save'
                        })

                        this.hideQuestionModal()
                    })
                    .catch(e => {
                        this.$q.loading.hide()

                        this.$q.notify({
                            message: this.$t('question_edit_error'),
                            type: 'negative',
                            icon: 'fas fa-ban'
                        })

                        console.log(e)
                    })
                }
                else {
                    this.$axios.post(`/admin_general_tests/general_tests/${this.id_test}/general_test_questions/`, data, this.getHeader)
                    .then(response => {
                        this.$q.loading.hide()
                        this.$v.$reset()

                        this.$q.notify({
                            message: this.$t('question_add_success'),
                            type: 'positive',
                            icon: 'far fa-save'
                        })

                        this.hideQuestionModal()
                    })
                    .catch(e => {
                        this.$q.loading.hide()

                        this.$q.notify({
                            message: this.$t('question_add_error'),
                            type: 'negative',
                            icon: 'fas fa-ban'
                        })

                        console.log(e)
                    })
                }
            }
        },

        addAlternative() {
            this.add_alternative = true
        },

        saveAlternative() {
            if (this.new_alternative) {
                let is_unique = true
                let is_correct = false

                this.alternatives.forEach(alternative => {
                    if (alternative.title == this.new_alternative) is_unique = false
                })

                if (is_unique) {
                    if (this.alternatives.length == 0) {
                        is_correct = true
                    }

                    this.alternatives.push({
                        title: this.new_alternative,
                        is_correct: is_correct,
                        actions: this.alternatives.length == 0 ? 0 : this.alternatives.length + 1
                    })
                }

                this.add_alternative = false
            }
            else {
                this.add_alternative = false
            }
            this.server_pagination.rowsNumber++
        },

        removeAlternative(row) {
            if (this.alternatives[row.__index].is_correct) {
                this.alternatives.splice(row.__index, 1)

                if (this.alternatives.length > 0) {
                    this.alternatives[0].is_correct = true
                }
            }
            else {
                this.alternatives.splice(row.__index, 1)
            }

            this.server_pagination.rowsNumber--
        },

        clearQuestions() {
            this.$v.$reset()
        },

        clearAlternative() {
            this.new_alternative = ''
        },

        changeCorrect(row) {
            this.alternatives.forEach(alternative => {
                if (alternative.actions != row.actions) alternative.is_correct = false
            })
        },

        getOrder(name) {
            if (name == 'cancel') {
                if (this.$q.platform.is.mobile) return 'order-last'
                else return 'order-first'
            }
            else if (name == 'save') {
                if (this.$q.platform.is.mobile) return 'order-first'
                else return 'order-last'
            }
        },

        hideQuestionModal() {
            this.title = ''
            this.alternatives = []
            this.$v.$reset()

            this.$emit('returnToQuestions', '')
        }
    },

    watch: {
        id_question: function() {
            if (this.id_question != 0) this.loadQuestion()
        }
    },

    validations() {
        return {
            title: { required },
        }
    }
}
</script>

<style>

</style>
